import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EnvConfig, Environment } from '@doc/core';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
// import { LoaderService } from 'libs/core/src/lib/loader.service';
import Swal from 'sweetalert2';
import { GlobalUploadQueueService } from './global-upload-queue.service';
import { UploadManagerService } from './upload-manager.service';
// import { SharedModuleService } from './shared-module.service';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  private readonly envConfig = inject(EnvConfig) as Environment;
  private readonly uploadManager = inject(UploadManagerService);
  private readonly globalUploadQueue = inject(GlobalUploadQueueService);
  private readonly warningAfterIdle = Math.max(this.envConfig.idleTime, 0);
  private readonly logoutAfterWarning = Math.max(this.envConfig.logoutTime, 0);
  private readonly totalIdleLimit = this.warningAfterIdle + this.logoutAfterWarning;

  private timeoutRef: ReturnType<typeof setTimeout> | undefined;
  private warningRef: ReturnType<typeof setTimeout> | undefined;
  private warningLogoutRef: ReturnType<typeof setTimeout> | undefined;
  private countdownInterval: ReturnType<typeof setInterval> | undefined;

  public isIdle = false;
  private isWarningVisible = false;
  private isLoggingOut = false;
  private activityListenersAttached = false;
  private uploadPauseActive = false;

  // #sharedModuleService = inject(SharedModuleService);
  private mouseHandler = () => this.onUserActivity();
  private keyHandler = () => this.onUserActivity();
  private touchHandler = () => this.onUserActivity();
  private clickHandler = () => this.onUserActivity();
  private scrollHandler = () => this.onUserActivity();

  constructor(
    private router: Router,
    // private loaderService: LoaderService
  ) {
    combineLatest([
      this.uploadManager.uploads$,
      this.globalUploadQueue.activeUploads$])
      .pipe(
        map(() => this.hasActiveUploadInProgress()),
        distinctUntilChanged()
      )
      .subscribe((hasUploadInProgress) => {
        if (hasUploadInProgress) {
          this.pauseForActiveUpload();
        } else {
          this.resumeAfterActiveUpload();
        }
      });
  }

  startWatching() {
    if (this.router.url.includes('/auth/login')) {
      this.stopWatching();
      Swal.close();
      return;
    }

    // Warning phase is a hard deadline: only "Stay Logged In" may extend the session.
    if (this.isWarningVisible) {
      return;
    }

    this.isLoggingOut = false;
    this.stopWatching();
    this.resetTimer();
    this.attachActivityListeners();
  }

  stopWatching() {
    this.isIdle = false;
    this.isWarningVisible = false;
    Swal.close();
    this.clearAllTimers();
    this.detachActivityListeners();
  }

  private attachActivityListeners() {
    if (this.activityListenersAttached) {
      return;
    }
    window.addEventListener('mousemove', this.mouseHandler);
    window.addEventListener('keydown', this.keyHandler);
    window.addEventListener('touchstart', this.touchHandler);
    window.addEventListener('click', this.clickHandler);
    window.addEventListener('scroll', this.scrollHandler, true);
    this.activityListenersAttached = true;
  }

  private detachActivityListeners() {
    if (!this.activityListenersAttached) {
      return;
    }
    window.removeEventListener('mousemove', this.mouseHandler);
    window.removeEventListener('keydown', this.keyHandler);
    window.removeEventListener('touchstart', this.touchHandler);
    window.removeEventListener('click', this.clickHandler);
    window.removeEventListener('scroll', this.scrollHandler, true);
    this.activityListenersAttached = false;
  }

  private onUserActivity() {
    if (this.isWarningVisible || this.uploadPauseActive) {
      return;
    }
    this.resetTimer();
  }

  private hasActiveUploadInProgress(): boolean {
    return (
      this.uploadManager.hasInProgressUploads() ||
      this.globalUploadQueue.hasInProgressUploads()
    );
  }

  private pauseForActiveUpload() {
    if (this.isWarningVisible || this.uploadPauseActive) {
      return;
    }
    this.uploadPauseActive = true;
    this.clearAllTimers();
  }

  private resumeAfterActiveUpload() {
    if (!this.uploadPauseActive) {
      return;
    }
    this.uploadPauseActive = false;
    if (
      this.isWarningVisible ||
      this.isLoggingOut ||
      this.router.url.includes('/auth/login') ||
      !localStorage.getItem('accessToken')
    ) {
      return;
    }
    this.resetTimer();
    if (!this.activityListenersAttached) {
      this.attachActivityListeners();
    }
  }

  private clearAllTimers() {
    clearTimeout(this.timeoutRef);
    clearTimeout(this.warningRef);
    clearTimeout(this.warningLogoutRef);
    clearInterval(this.countdownInterval);
    this.timeoutRef = undefined;
    this.warningRef = undefined;
    this.warningLogoutRef = undefined;
    this.countdownInterval = undefined;
  }

  private resetTimer() {
    if (this.isWarningVisible || this.uploadPauseActive) {
      return;
    }

    this.isIdle = false;
    this.clearAllTimers();

    this.warningRef = setTimeout(() => this.showWarning(), this.warningAfterIdle);
    this.timeoutRef = setTimeout(() => {
      this.triggerIdleLogout();
    }, this.totalIdleLimit);
  }

  private showWarning() {
    if (this.isWarningVisible || this.uploadPauseActive) {
      return;
    }

    this.isWarningVisible = true;
    this.detachActivityListeners();
    clearTimeout(this.timeoutRef);
    this.timeoutRef = undefined;
    clearTimeout(this.warningRef);
    this.warningRef = undefined;

    this.warningLogoutRef = setTimeout(() => {
      Swal.close();
      this.triggerIdleLogout();
    }, this.logoutAfterWarning);

    let timeLeft = Math.floor(this.logoutAfterWarning / 1000);

    const lang = localStorage.getItem('lang') === 'ar' ? 'ar' : 'en';
    const isArabic = lang === 'ar';

    const title = isArabic ? 'تحذير انتهاء الجلسة' : 'Session Timeout Warning';
    const description = isArabic
      ? 'سيتم تسجيل خروجك بسبب عدم النشاط.'
      : 'You will be logged out due to inactivity.';
    const confirmButtonText = isArabic ? 'ابقَ مسجّلًا' : 'Stay Logged In';
    const cancelButtonText = isArabic ? 'تسجيل الخروج الآن' : 'Logout Now';

    const totalWarningSeconds = Math.floor(this.logoutAfterWarning / 1000);

    Swal.fire({
      html: `
        <div dir="${isArabic ? 'rtl' : 'ltr'}" class="idle-timeout-modern">
          <div class="idle-timeout-icon-ring">
            <svg class="idle-timeout-progress-ring" viewBox="0 0 80 80">
              <circle class="idle-timeout-progress-bg" cx="40" cy="40" r="36" />
              <circle id="idleProgressCircle" class="idle-timeout-progress-fill" cx="40" cy="40" r="36"
                stroke-dasharray="226.19" stroke-dashoffset="0" />
            </svg>
            <i class="pi pi-clock idle-timeout-clock-icon"></i>
          </div>
          <h2 class="idle-timeout-title">${title}</h2>
          <p class="idle-timeout-desc">${description}</p>
          <div class="idle-timeout-countdown" id="idleCountdown">${this.formatTime(timeLeft)}</div>
        </div>
      `,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      footer: `
        <div class="idle-timeout-actions" dir="${isArabic ? 'rtl' : 'ltr'}">
          <button type="button" id="idleStayBtn" class="idle-timeout-btn idle-timeout-btn--primary">
            <i class="pi pi-shield"></i> ${confirmButtonText}
          </button>
          <button type="button" id="idleLogoutBtn" class="idle-timeout-btn idle-timeout-btn--danger">
            <i class="pi pi-sign-out"></i> ${cancelButtonText}
          </button>
        </div>
      `,
      backdrop: 'rgba(15, 23, 42, 0.6)',
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        container: 'idle-timeout-swal-container',
        popup: 'idle-timeout-swal-popup idle-timeout-modern-popup',
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.setAttribute('dir', isArabic ? 'rtl' : 'ltr');
        }

        const stayBtn = document.getElementById('idleStayBtn');
        const logoutBtn = document.getElementById('idleLogoutBtn');
        if (stayBtn) stayBtn.addEventListener('click', () => {
          (Swal.getConfirmButton() as HTMLButtonElement)?.click();
        });
        if (logoutBtn) logoutBtn.addEventListener('click', () => {
          (Swal.getCancelButton() as HTMLButtonElement)?.click();
        });

        const circle = document.getElementById('idleProgressCircle') as unknown as SVGCircleElement | null;
        const circumference = 2 * Math.PI * 36; // ~226.19

        this.countdownInterval = setInterval(() => {
          timeLeft--;
          const el = document.getElementById('idleCountdown');
          if (el) el.innerText = this.formatTime(timeLeft);

          if (circle) {
            const progress = timeLeft / totalWarningSeconds;
            circle.style.strokeDashoffset = String(circumference * (1 - progress));
          }

          if (timeLeft <= 0) {
            Swal.close();
            this.triggerIdleLogout();
          }
        }, 1000);
      }
    }).then(result => {
  clearInterval(this.countdownInterval);
  this.countdownInterval = undefined;
  clearTimeout(this.warningLogoutRef);
  this.warningLogoutRef = undefined;
  this.isWarningVisible = false;

  if (this.isLoggingOut) {
    return;
  }

  if (result.isConfirmed) {
    this.attachActivityListeners();
    this.resetTimer();
  } else {
    this.triggerIdleLogout();
  }
});

  }

  private triggerIdleLogout() {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    this.isWarningVisible = false;
    this.stopWatching();

    this.isIdle = true;
    // this.loaderService.toggleLoader(false);

    localStorage.clear();
    sessionStorage.clear();
    Swal.close(); 
    // this.#sharedModuleService.clearModules();
    this.router.navigate(['/auth/login']).then(() => {
    setTimeout(() => {
      location.reload();   // Full browser reload
    }, 10);
  });
  }

  private formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
}
